import { inject, Injectable } from '@angular/core';

import { translate } from '@jsverse/transloco';

import {
  Collection,
  CollectionCreateOptions,
  CollectionService,
  SectionMetadata,
  SectionSeparator,
} from 'app/providers/storage-api/collection.service';
import {
  ContentChannel,
  ContentChannelService,
} from 'app/providers/storage-api/content-channel.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Project } from 'app/providers/storage-api/project.service';
import {
  CollectionFormValue,
  SectionField,
  UploadCreateDialogResult,
} from './upload-create.component';

export const NO_SECTION_SEPARATOR_ID = '_none';
export const DEFAULT_CONTENT_CHANNEL = 'CC025';

export interface ContentChannelsForUi {
  contentChannels: ContentChannel[];
  node1: ContentChannel;
  node2: ContentChannel;
  node3: ContentChannel;
}

@Injectable()
export class CollectionConfigurationService {
  private readonly _collectionService = inject(CollectionService);
  private readonly _contentChannelService = inject(ContentChannelService);

  async getContentChannelsForUi(collection?: Collection): Promise<ContentChannelsForUi> {
    const root = await this._contentChannelService.getContentChannels();

    const contentChannel = collection?.contentChannel ?? DEFAULT_CONTENT_CHANNEL;
    const ccBranch = CollectionConfigurationService.getContentChannelBranch(root, contentChannel);

    return {
      contentChannels: root.children,
      node1: ccBranch[0],
      node2: ccBranch[1],
      node3: ccBranch[2],
    };
  }

  async getCollectionFacet(
    projectId: string,
    collectionName: string,
    fieldName: string
  ): Promise<SectionMetadata> {
    return this._collectionService.getSectionMetadataTopN(projectId, collectionName, fieldName);
  }

  async save(
    projectId: string,
    formValue: CollectionFormValue,
    collection?: Collection
  ): Promise<UploadCreateDialogResult> {
    let savedCollection: Collection;
    if (collection) {
      savedCollection = await this.updateCollection(collection, formValue);
    } else {
      savedCollection = await this.createCollection(projectId, formValue);
    }

    const result = {
      collectionName: savedCollection.collectionName,
      displayName: savedCollection.displayName,
      showUpload: formValue.followUp === 'upload',
    };

    return result;
  }

  private createCollection(projectId: string, formValue: CollectionFormValue): Promise<Collection> {
    const collectionCreateOptions: CollectionCreateOptions = {
      name: formValue.name.trim(),
      notes: formValue.notes,
      contentChannel: CollectionConfigurationService.getLowestContentChannelId(
        formValue.contentChannel1,
        formValue.contentChannel2,
        formValue.contentChannel3
      ),
    };

    if (formValue.followUp === 'upload') {
      collectionCreateOptions.origin = 'UPLOAD';
    }

    return this._collectionService.createCollection(projectId, collectionCreateOptions);
  }

  private updateCollection(
    collection: Collection,
    formValue: CollectionFormValue
  ): Promise<Collection> {
    const sectionSeparator = CollectionConfigurationService.createSectionSeparator(
      formValue.sectionSeparator
    );
    const focusedSpeakerName = CollectionConfigurationService.createFocusedSpeakerName(
      formValue.focusedSpeaker
    );

    const collectionToUpdate: Collection = {
      ...collection,
      displayName: formValue.name.trim(),
      notes: formValue.notes,
      contentChannel: CollectionConfigurationService.getLowestContentChannelId(
        formValue.contentChannel1,
        formValue.contentChannel2,
        formValue.contentChannel3
      ),
      sectionSeparator,
      focusedSpeakerName,
    };

    return this._collectionService.updateCollection(collectionToUpdate);
  }

  static createSectionFields(collection: Collection): SectionField[] {
    const sectionFields: SectionField[] = [
      { label: translate('upload-create.no-section-separator'), value: NO_SECTION_SEPARATOR_ID },
    ];

    sectionFields.push(
      ...collection.sectionFieldNames
        .map((f) => FieldService.getField(f))
        .filter((f) => f.fieldType === 'keyword')
        .map((f) => ({
          value: f.fieldName,
          label: f.label,
        }))
        .sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()))
    );

    return sectionFields;
  }

  async createFocusSectionFields(
    collection: Collection,
    facetMetadataName?: string,
    projectId?: string
  ): Promise<SectionField[]> {
    let focusCandidateFields: string[] = [];
    try {
      const facets = await this.getCollectionFacet(
        projectId,
        collection.collectionName,
        facetMetadataName
      );
      const facetItems = facets?.items ?? [];
      if (facetItems.length) {
        focusCandidateFields = facetItems
          .map((item) => item?.value)
          .filter((value): value is string => !!value);
      }
    } catch (error) {
      throw error;
    }

    const focusSectionFields = focusCandidateFields
      .map((field) => ({
        value: field,
        label: field,
      }))
      .sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()));
    focusSectionFields.unshift({
      label: translate('upload-create.no-focus-separator'),
      value: NO_SECTION_SEPARATOR_ID,
    });

    return focusSectionFields;
  }

  static getExistingCollectionNames(project: Project, collection?: Collection): string[] {
    let existingCollectionNames = project.collections.map((c) => c.displayName);

    if (collection) {
      existingCollectionNames = existingCollectionNames.filter((n) => n !== collection.displayName);
    }

    return existingCollectionNames;
  }

  private static getLowestContentChannelId(...contentChannels: ContentChannel[]): string {
    for (let i = contentChannels.length - 1; i >= 0; i--) {
      if (contentChannels[i]) {
        return contentChannels[i].id;
      }
    }
  }

  private static getContentChannelBranch(root: ContentChannel, leafId: string): ContentChannel[] {
    const ccBranch: ContentChannel[] = [];

    const dfs = (cc: ContentChannel): ContentChannel => {
      if (cc.id === leafId) {
        return cc;
      }

      if (!cc.children) {
        return;
      }

      for (const child of cc.children) {
        const node = dfs(child);
        if (node) {
          ccBranch.unshift(node);
          return cc;
        }
      }
    };

    dfs(root);

    return ccBranch;
  }

  private static createSectionSeparator(sectionSeparator: string): SectionSeparator | undefined {
    if (!sectionSeparator || sectionSeparator === NO_SECTION_SEPARATOR_ID) {
      return undefined;
    }

    return {
      label: sectionSeparator,
      displayAs: FieldService.getFieldLongName(sectionSeparator),
    };
  }

  private static createFocusedSpeakerName(focusedSpeaker?: string): string | undefined {
    if (!focusedSpeaker || focusedSpeaker === NO_SECTION_SEPARATOR_ID) {
      return undefined;
    }

    return focusedSpeaker;
  }
}
